// GPU availability and prices (ui-spec §6.13): the live Offering watch as a grid with one card per GPU shape
// (family, memory and count), for Instances › GPUs and the launch picker. Form factors and region
// classes of a shape merge into its card, which shows the price and host of its lead: the cheapest member with a
// machine free now, as default placement takes it, else the cheapest member. Offerings are customer-safe capacity
// classes, never providers (ADR-011).
import { useKList } from "@nodus/api";
import {
  AppLink,
  cliCommand,
  EmptyState,
  Button,
  ListSkeleton,
  formatClock,
  formatRate,
  gpuName,
  offeringInStock,
  offeringFamily,
  uniqueOfferings,
  parseMicros,
  pricesAsOf,
  Select,
  usePrefs,
  variantsName,
  type OfferingLike,
} from "@nodus/ui";
import * as React from "react";
import { ApiErrorCallout } from "../../app/api-error";
import { selectionForOffering, supportsCompute } from "./compute-selection";

type Availability = "Available" | "Limited" | "Unavailable";

export interface GpuShape {
  key: string;
  family: string;
  /** The GPU as people read it, such as "NVIDIA RTX 4090 · 24 GB". */
  name: string;
  /** The display names of the variants merged into this shape, when there is more than one. */
  variants?: string[];
  memory?: string;
  count: number;
  /** The cheapest member with a machine free now, else the cheapest: its price, host and interruptibility show. */
  lead: OfferingLike;
  availableNow: number;
  availability: Availability;
}

const PAGE = 24;
const RANK: Record<Availability, number> = { Available: 2, Limited: 1, Unavailable: 0 };
const DOT: Record<Availability, string> = { Available: "bg-success-dot", Limited: "bg-warning-dot", Unavailable: "bg-neutral-dot" };

/** A memory quantity as marketed, "80Gi" and "80 GB" both reading "80 GB". */
export function memoryLabel(q: string | undefined): string | undefined {
  const m = q ? /^(\d+(?:\.\d+)?)\s*(?:Gi|G|GB|GiB)?$/i.exec(q.trim()) : null;
  return m ? `${m[1]} GB` : q;
}

const price = (o: OfferingLike) => parseMicros(o.status?.priceUSDPerHour?.from) ?? Infinity;

function availabilityOf(o: OfferingLike): Availability {
  const n = o.status?.availableNow ?? 0;
  return o.status?.availability ?? (n >= 4 ? "Available" : n > 0 ? "Limited" : "Unavailable");
}

/** Whether o leads a shape over the current lead: in stock first, then the lower price. */
const leads = (o: OfferingLike, lead: OfferingLike) =>
  offeringInStock(o) !== offeringInStock(lead) ? offeringInStock(o) : price(o) < price(lead);

/** One shape per family, memory and count, by its lead's price. */
export function gpuShapes(offerings: readonly OfferingLike[]): GpuShape[] {
  const byKey = new Map<string, GpuShape>();
  for (const o of uniqueOfferings(offerings)) {
    if ((o.spec?.category ?? "GPU") !== "GPU") continue;
    const family = offeringFamily(o);
    const memory = memoryLabel(o.spec?.accelerator?.memory);
    const count = o.spec?.count ?? 1;
    const key = `${family}|${memory ?? ""}|${count}`;
    const s = byKey.get(key);
    const a = availabilityOf(o);
    if (!s) {
      const name = o.spec?.accelerator?.displayName ?? `${family}${memory ? ` · ${memory}` : ""}`;
      byKey.set(key, { key, family, name, ...(memory ? { memory } : {}), count, lead: o, availableNow: o.status?.availableNow ?? 0, availability: a });
      continue;
    }
    s.availableNow += o.status?.availableNow ?? 0;
    // Variants that share a family and memory (A100 SXM and PCIe) read as the family ("NVIDIA A100 · 80 GB").
    const dn = o.spec?.accelerator?.displayName;
    if (dn) s.variants = [...new Set([...(s.variants ?? [s.name]), dn])];
    if (RANK[a] > RANK[s.availability]) s.availability = a;
    if (leads(o, s.lead)) s.lead = o;
  }
  for (const s of byKey.values()) if (s.variants) s.name = variantsName(s.variants);
  return [...byKey.values()].sort((a, b) => price(a.lead) - price(b.lead) || a.family.localeCompare(b.family) || a.count - b.count);
}

const shapeTitle = (s: GpuShape) => `${s.count} × ${s.name}`;

function hostLine(o: OfferingLike): string | undefined {
  const cpu = o.spec?.host?.cpu;
  const mem = memoryLabel(o.spec?.host?.memory);
  return [cpu !== undefined ? `${cpu} vCPU` : "", mem ? `${mem} RAM` : ""].filter(Boolean).join(" · ") || undefined;
}

function ShapeCard({ shape, href }: { shape: GpuShape; href: string }) {
  const title = shapeTitle(shape);
  const host = hostLine(shape.lead);
  return (
    <li
      aria-label={title}
      className="relative flex min-w-0 flex-col rounded-lg border border-line bg-card p-6 transition-colors focus-within:border-line-strong hover:border-line-strong"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 text-[18px] leading-6 font-semibold tracking-[var(--nd-tracking-display)] text-fg-primary">
          {/* The whole card is the link to a new instance on this shape. */}
          <AppLink href={href} className="nd-stretched">
            {title}
          </AppLink>
        </h3>
        <span className="inline-flex shrink-0 items-center gap-1.5 pt-0.5 text-small text-fg-muted">
          <span className={`size-1.5 rounded-full ${DOT[shape.availability]}`} aria-hidden />
          {shape.availability}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-small text-fg-muted">
        {host ? <span>{host}</span> : null}
        {shape.lead.spec?.interruptible ? <span className="inline-flex h-5 items-center rounded-md border border-line px-1.5">interruptible</span> : null}
      </div>
      <div className="flex-1" />
      <div className="mt-5 flex items-baseline justify-between gap-3 border-t border-line-subtle pt-4">
        <span className="text-ui text-fg-muted">
          from <span className="font-mono text-[18px] font-semibold text-fg-primary tabular-nums">{formatRate(shape.lead.status?.priceUSDPerHour?.from)}</span>
        </span>
        <span className="shrink-0 text-small text-fg-muted tabular-nums">{shape.availableNow} available now</span>
      </div>
    </li>
  );
}

export function GpuAvailability({ org, project }: { org: string; project: string }) {
  const { timeZone } = usePrefs();
  const offerings = useKList("Offering", { org }, { watch: false, relistEvery: 60_000 });
  const items = offerings.data?.items as OfferingLike[] | undefined;
  const shapes = React.useMemo(() => gpuShapes((items ?? []).filter((offering) => supportsCompute(offering, "workspaces"))), [items]);
  const [family, setFamily] = React.useState("all");
  const [count, setCount] = React.useState("all");
  const [shown, setShown] = React.useState(PAGE);
  const filtered = shapes.filter((s) => (family === "all" || s.family === family) && (count === "all" || String(s.count) === count));
  const sentinel = React.useRef<HTMLDivElement>(null);
  const more = shown < filtered.length;
  React.useEffect(() => {
    const el = sentinel.current;
    if (!el || !more) return;
    const io = new IntersectionObserver(
      (e) => {
        if (e[0]?.isIntersecting) setShown((n) => n + PAGE);
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [more, shown]);

  if (offerings.isLoading) return <ListSkeleton rows={3} />;
  if (offerings.error) return <ApiErrorCallout error={offerings.error} scope={{ org, project }} onRetry={() => void offerings.refetch()} />;
  if (offerings.isSuccess && shapes.length === 0) {
    return <EmptyState title="No GPU offerings right now" cli={cliCommand(["get", "gpus"], { org, project })} />;
  }
  const asOf = pricesAsOf(items ?? []);
  const base = `/${encodeURIComponent(org)}/${encodeURIComponent(project)}/instances/new`;
  const option = (v: string, label = v) => ({ value: v, label });
  const filter = (set: (v: string) => void) => (v: string) => {
    set(v);
    setShown(PAGE);
  };
  return (
    <div className="@container">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select label="GPU" value={family} onValueChange={filter(setFamily)} options={[option("all", "Any GPU"), ...[...new Map(shapes.map((s) => [s.family, gpuName(s.family, items ?? [])])).entries()].sort().map(([f, n]) => option(f, n))]} />
        <Select
          label="GPU count"
          value={count}
          onValueChange={filter(setCount)}
          options={[option("all", "Any count"), ...[...new Set(shapes.map((s) => s.count))].sort((a, b) => a - b).map((n) => option(String(n), `${n} × GPU`))]}
        />
        <span className="ml-auto text-small text-fg-muted">{asOf ? `Prices as of ${formatClock(asOf, timeZone)} · updates live` : "Updates live"}</span>
      </div>
      {filtered.length ? (
        <ul aria-label="GPUs" className="grid grid-cols-1 gap-4 @min-[34rem]:grid-cols-2 @min-[56rem]:grid-cols-3">
          {filtered.slice(0, shown).map((s) => (
            <ShapeCard key={s.key} shape={s} href={`${base}?${new URLSearchParams(Object.entries(selectionForOffering(s.lead)).filter(([, value]) => value !== undefined).map(([key, value]) => [key, String(value)]))}`} />
          ))}
        </ul>
      ) : offerings.isSuccess ? (
        <EmptyState title="No GPUs match" actions={<Button variant="outline" onClick={() => { setFamily("all"); setCount("all"); }}>Clear filters</Button>} />
      ) : null}
      {more ? <div ref={sentinel} aria-hidden className="h-px" /> : null}
    </div>
  );
}
